import { computed, ref } from "vue";
import {
  vaccineApi,
  type BookVaccineAppointmentInput,
  type VaccineAppointmentView,
  type VaccineRecommendationView,
  type VaccineRecordView,
  type VaccineSummaryView,
  type VaccineTypeView,
} from "@/api/vaccine.api";

// Record decorated with a computed pill state for the FE badge.
export interface VaccineRecordWithStatus extends VaccineRecordView {
  statusKind: "complete" | "pending";
}

// Phase 8.2 — real customer-side vaccine composable. Module-scoped state
// so all four sections of the CustomerVaccinePage share one fetch round.
//
// Strategy: every page mount triggers refreshAll() which fans out the 4
// endpoints in parallel. Errors are non-fatal — partial sections still
// render, the affected ref simply stays empty. The page renders an empty
// state when the customer has no records yet (FE doesn't fall back to
// mock data — vaccine FE is wired to live data only, by design).

const summary = ref<VaccineSummaryView | null>(null);
const records = ref<VaccineRecordView[]>([]);
const recommendations = ref<VaccineRecommendationView[]>([]);
const upcoming = ref<VaccineAppointmentView[]>([]);
const catalog = ref<VaccineTypeView[]>([]);
const loading = ref(false);
const error = ref<string | null>(null);

// Run all 4 reads in parallel. Per-section errors are swallowed so a
// failing recommendations call doesn't blank out the records section.
async function refreshAll(): Promise<void> {
  loading.value = true;
  error.value = null;
  const [s, r, rec, up] = await Promise.allSettled([
    vaccineApi.summary(),
    vaccineApi.records(),
    vaccineApi.recommendations(),
    vaccineApi.upcoming(),
  ]);
  if (s.status === "fulfilled") summary.value = s.value;
  else error.value = s.reason?.message ?? "summary failed";
  records.value = r.status === "fulfilled" ? r.value : [];
  recommendations.value = rec.status === "fulfilled" ? rec.value : [];
  upcoming.value = up.status === "fulfilled" ? up.value : [];
  loading.value = false;
}

// Decorate one row with the pill-state derived from doseNumber + totalDoses.
function decorate(r: VaccineRecordView): VaccineRecordWithStatus {
  const isLastDose = r.doseNumber >= r.totalDoses;
  const hasNextDose = !!r.nextDueAt;
  return {
    ...r,
    statusKind: !hasNextDose && isLastDose ? "complete" : "pending",
  };
}

// Group records by Thai Buddhist year for the "ปี 256X" sectioning in the FE.
function groupRecordsByThaiYear(): Array<{
  year: number;
  rows: VaccineRecordWithStatus[];
}> {
  const buckets = new Map<number, VaccineRecordWithStatus[]>();
  for (const r of records.value) {
    // Convert ISO Gregorian -> Thai BE year.
    const beYear = new Date(r.givenAt).getUTCFullYear() + 543;
    const arr = buckets.get(beYear) ?? [];
    arr.push(decorate(r));
    buckets.set(beYear, arr);
  }
  return Array.from(buckets.entries())
    .sort((a, b) => b[0] - a[0])
    .map(([year, rows]) => ({ year, rows }));
}

// Book a vaccine appointment then refresh summary + upcoming.
async function book(
  input: BookVaccineAppointmentInput,
): Promise<VaccineAppointmentView> {
  const res = await vaccineApi.book(input);
  await refreshAll();
  return res;
}

// Cancel a SCHEDULED appointment then refresh.
async function cancel(appointmentId: string): Promise<void> {
  await vaccineApi.cancel(appointmentId);
  await refreshAll();
}

async function loadCatalog(): Promise<void> {
  try {
    catalog.value = await vaccineApi.catalog();
    error.value = null;
  } catch (e: any) {
    console.error("Failed to load vaccine catalog", e);
    error.value = "Catalog error: " + (e.message || String(e));
  }
}

export function useVaccine() {
  // Convenience: pillstate per record for the FE badge ("ครบ" / "รอเข็ม N").
  const recordsWithStatus = computed(() => records.value.map(decorate));

  // Number of items in each tab — feeds the "ฉีดแล้ว (N) / แนะนำ (M)" label.
  const tabCounts = computed(() => ({
    done: records.value.length,
    recommended: recommendations.value.length,
  }));

  return {
    summary,
    records,
    recordsWithStatus,
    recommendations,
    upcoming,
    catalog,
    loading,
    error,
    tabCounts,
    refreshAll,
    loadCatalog,
    groupRecordsByThaiYear,
    book,
    cancel,
  };
}
